<template>
  <div class="container">
    <div class="row">
      <div class="col 2-col">
        <h2 id="waterresources">2. Water Resources</h2>
        <h3>2.1 Water Use and Primary Productivity</h3>
        <p>
          This section shows information about annual water use, water use
          trends, and primary production in the period 2018-2023. The
          information is based on WAPOR Level 2 data. The trend map cannot be
          used to deduce long term trends, but can be helpful in identifying
          areas where water use has changed dramatically in the past few years,
          for example increases due to new irrigation schemes or decreases due
          to deforestation.
        </p>
        <RasterMap
          backgrounds="light,osm"
          title="Water Use and Primary Production Maps"
          attribution="Map: Geofolio. Data: FAO WAPOR. Backgrounds: Mapbox/Openstreetmap Contributors [2]."
          :legendFirst="false"
          v-bind:layout="{
            legendWidth: 4,
            legendCols: 2,
            descriptionWidth: 8,
            order: ['legend', 'description'],
          }"
          v-bind:location="location"
          v-bind:factsheet="factsheet"
          v-bind:loaded="loaded"
          v-bind:layers="waterUseLayers"
          :contextMenu="['explore', 'table', 'info']"
        >
        </RasterMap>

        <h3>2.1 Surface Water Dynamics</h3>
        <p>
          This section provides information about surface water dynamics in the
          area. It can be used to assess where surface water is present, what
          type of surface water it is (seasonal, permanent), and what long term
          trends are visible, such as whether it is increasing or decreasing.
        </p>
        <RasterMap
          backgrounds="light,osm"
          title="Surface Water Dynamics"
          attribution="Map: Geofolio. Data: JRC Global Surface Water Dynamics. Backgrounds: Mapbox/Openstreetmap Contributors [2]."
          v-bind:layout="{
            legendWidth: 4,
            legendCols: 2,
            descriptionWidth: 8,
            order: ['legend', 'description'],
          }"
          v-bind:location="location"
          v-bind:factsheet="factsheet"
          v-bind:loaded="loaded"
          v-bind:layers="surfaceWaterLayers"
          :contextMenu="['explore', 'table', 'info']"
        >
        </RasterMap>

        <h2 id="swc">3. Soil and Water Conservation</h2>
        <h3>3.1 Runoff and Erosion Risk</h3>
        <p>
          In addition to information about water resources, it is also possible
          to estimate other impacts that follow from precipitation and water
          use. The maps below show runoff estimates (i.e. how much precipitation
          runs off in a year) and erosion risk estimates (i.e. which areas are
          sensitive to soil erosion). This information can used to indicate
          hotspots where water carrying sediments or flooding may originate, or
          areas where excessible soil erosion may pose a risk to agricultural
          productivity.
        </p>
        <RasterMap
          backgrounds="light,osm"
          title="Runoff and Erosion Risk"
          attribution="Map: Geofolio. Data: Geofolio/GCN250/FAO-WAPOR/SRTM/SoilGrids/JRC-GLOREDA. Backgrounds: Mapbox/Openstreetmap Contributors [2]."
          v-bind:layout="{
            legendWidth: 4,
            legendCols: 2,
            descriptionWidth: 8,
            order: ['legend', 'description'],
          }"
          v-bind:location="location"
          v-bind:factsheet="factsheet"
          v-bind:loaded="loaded"
          v-bind:layers="runoffLayers"
          :contextMenu="['explore', 'table', 'info']"
        >
        </RasterMap>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

import _ from "lodash";

import RasterMap from "@/components/factsheet/RasterMap.vue";
export default {
  name: "WaterResources",
  components: { RasterMap },
  props: ["location", "factsheet", "loaded"],
  data() {
    return {
      waterUseLayers: [
        {
          title: "Water Use (Average, 2018-2023)",
          source: "/fao_wapor/L2-AETI-A-MEAN.tif",
          long_description:
            "<p>This layer shows an average of the annual <strong>Actual Evapotranspiration (AET)</strong> in <strong>mm/yr</strong> for the <strong>period 2028-2023</strong>. AET can be used to estimate the water consumption of vegetation, either natural or for agricultural production.</p><p>Areas in light <strong>blue/green represent low water consumption</strong>, these can be dry or barren areas, or areas with a high fraction of built-up/urban areas. Areas in <strong>dark blue represent high water consumption</strong>, these are typically irrigated agricultural areas or areas with dense tree cover.</p>",
        },
        {
          title: "Water Use (Trend, 2018-2023)",
          source: "/fao_wapor/L2-AETI-A-TREND.tif",
          long_description:
            "<p>This layer shows a <strong>water use trend</strong> in the period <strong>2018-2023</strong>. The trend map can be used to identify hotspots where water consumption either <strong>increased or decreased</strong> significantly during the period <strong>2018-2023</strong>.</p><p>The variation between years is partly due to natural variations, with one year being randomly wetter or drier than the other. However, the outliers can also be due to other causes. For example, <strong>dark red areas indicate strong decreases in water use</strong>, which can be due to land abandonment, desertification, or drainage of wetlands. Outliers in <strong>dark green represent strong increases in water use</strong>, which can be caused by development of new irrigation schemes (or re-establishing old ones), new groundwater extraction, reforestation, or preventing runoff with water harvesting techniques.</p><p>You can <strong>right-click the map</strong> to explore before and after satellite imagery of an interesting location to help you identify the cause of the trend.</p>",
        },
        /*{
                'title': 'Water Use (Monthly Avg.)', 
                'source': '/fao_wapor/L2-AETI-M-MEAN.tif', 
                'long_description': "<p>Actual ET Monthly</p>",
                'time': 'monthly',
                'time_slice': 1
              },*/
        {
          title: "Primary Production (Average, 2018-2023)",
          source: "/fao_wapor/L2-NPP-A-MEAN.tif",
          long_description:
            "<p>This layer shows an average of the annual <strong>Net Primary Production (NPP)</strong> in <strong>grams of carbon per square meter per year (gC/m²/year)</strong>. This represents the conversion of carbon dioxide from the atmosphere into biomass my photosynthesis.</p><p>You can <strong>right-click the map</strong> to explore satellite imagery of an interesting location to help you identify areas with high NPP.</p>",
        },
      ],
      runoffLayers: [
        {
          title: "Runoff Estimate (Annual)",
          source: "/geofolio_runoff/GCN250Runoff-A.tif",
          long_description:
            "<p>This layer shows an estimate of <strong>average runoff</strong> in <strong>mm/year</strong>. This is a modelled data product at <strong>250m resolution</strong> based on a global curve-number (CN) method.</p><p>While the maps give an indication of annual runoff, the data must be used with care at local scales, as various factors which affect runoff at the landscape level may not be representated accurately.</p>",
        },
        {
          title: "Erosion Risk Estimate (Annual)",
          source: "/geofolio_erosion/e_annual_mercator.tif",
          long_description:
            "<p>This layer shows a <strong>Soil Erosion Risk Estimate</strong> which was calculated based on global datasets of commonly used erosion risk factors, such as <strong>slope, land cover, soil erodibility</strong>. In addition to static data, <strong>monthly estimates of rainfall erosivity and vegetation cover</strong> were used to capture seasonal variations where high rainfall erosivity coincides with low vegetation cover.</p><p>Areas with <strong>high erosion risk are orange and red</strong> and are typically areas on steep slopes, with soils sensitive to erosion, and with poor soil protection (such as vegetation cover) in place. Low risk areas are shown in <strong>green and pale green</strong>.</p><p>You can <strong>right-click the map</strong> to see satellite imagery of high risk areas. Exploring imagery can be helpful in identifying a root cause for the high risk factor: land degradation, overgrazing, or simply a combination of topography, soils, or landforms.</p>",
        },
        {
          title: "Precipitation (Annual)",
          source: "/worldclim_precipitation/prec-annual.tif",
          long_description:
            "<p>This layer shows <strong>annual precipitation</strong> in <strong>mm/year</strong> at a <strong>1km resolution</strong>. The data is from the WorldClim dataset and is representative for the <strong>period 1970-2000</strong>. Areas with relatively <strong>high precipitation are shown in dark blue</strong>, with <strong>low precipitation shown in pale yellow</strong>.</p><p>When viewing this map, <strong>be aware of the range (minimum, maximum) of precipitation values</strong>, as blue areas in the map may not necessarily mean that the precipitation is high in absolute terms, for example in deserts and very arid areas the map may show blue even for extremely low values (and the opposite in extremely wet areas).</p><p>Within an area of interest, <strong>variations in precipitation are strongly influenced by topography</strong>, and it is common to expect higher precipitation estimates in mountainous and hilly regions compared to the lower flat regions in the same area of interest.",
        } /*,
              {
                'title': 'Runoff Estimate (Monthly)', 
                'source': '/geofolio_runoff/GCN250Runoff-M.tif', 
                'long_description': "<p>Monthly runoff</p>",
                'time': 'monthly',
                'time_slice': 1
              },*/,
      ],
      surfaceWaterLayers: [
        {
          title: "Water Occurrence (1984-2021)",
          source: "/jrc_surface_water_dynamics/occurrence.tif",
          long_description:
            "<p>This layer shows the <strong>Water Occurrence in the period 1984-2021</strong> on a <strong>scale of 0% to 100%</strong>. It captures both the intra- and inter-annual variability and changes in surface water presence.</p><p>Light and pale blues represent areas where water occurs rarely or less frequently. Dark blue represents permanent or near-permanent water areas, where there has always or very frequently been water observed in the period from 1984 to 2021.</p>",
        },
        {
          title: "Water Change",
          source: "/jrc_surface_water_dynamics/change.tif",
          long_description:
            "<p>This layer shows the <strong>Water Occurrence Change Intensity</strong>, which indicates where surface water occurrence <strong>increased, decreased, or remained the same</strong> in the <strong>period 1984-1999 compared to 2000-2021</strong>.</p> Increases in water occurrence are shown in blues, areas with little or no change in pale yellow, and areas where water occurrence decreased are shown in red. Changes in water occurrence can occur due to natural causes such as meandering rivers or changing coastlines, but may also have other root causes and manifest itself as reservoirs or lakes drying up, wetlands being drained, or land degradation causing increased runoff and/or flooding.</p><p>You can <strong>right-click the map</strong> to see satellite imagery of a particular location to support such a hypothesis.</p> ",
        },
        {
          title: "Water Seasonality",
          source: "/jrc_surface_water_dynamics/seasonality.tif",
          long_description:
            "<p>This layer shows <strong>water seasonality</strong> for the most recent year (2021) and shows indicates whether a water surface is permanently water, or that it is water only for several (1-11) months per year. <strong>Permanent water is shown in dark blue</strong> and <strong>seasonal waters are shown in light/pale shades of blue</strong>.</p>",
        },
        {
          title: "Water Transitions",
          source: "/jrc_surface_water_dynamics/transitions.tif",
          long_description:
            "<p>This layer shows <strong>water transitions</strong> between <strong>the first and last years in the dataset</strong>. It captures the change trajectories between three main water classes: <strong>permanent water, seasonal water, and non-water</strong>. The last year of the transition is always 2021, and the initial year that is compared against is the first yera after 1984 that enough observations were available.</p><p>You can <strong>right-click the map</strong> to explore satellite imagery of any interesting locations.</p>",
        },
      ],
    };
  },
  created() {},
  computed: {},
  mounted() {},
  methods: {},
};
</script>

<style>
div.map {
  width: 100%;
  height: 450px;
  display: block;
}
</style>
